// Cele 5 pagini de departament, trilingv (en/ro/de). `slug`, `demoKey` și `flags`
// (eticheta de piață RO/CH per problemă) sunt agnostice de limbă; restul se traduce.
// DE = ortografie elvețiană (ss, nu ß) + „Sie", pentru clienții CH.
// RO + DE draftate de mine — de revizuit de Matei (mai ales DE pentru piața CH).

import { DEFAULT_LOCALE } from '../i18n/index.js'

export const DEPARTMENTS = [
  {
    slug: 'finance',
    demoKey: 'close-cockpit',
    flags: ['ro', 'ro', 'ch', 'ch'],
    i18n: {
      en: {
        label: 'Finance',
        hero: "Your close shouldn't live in twelve spreadsheets and one person's head.",
        problems: [
          'Month-end close runs on a fragile Excel only one person understands.',
          'Group / multi-entity consolidation with no single board; reporting packs assembled by hand for the board or your PE sponsor.',
          "Reconciliations NetSuite or QuickBooks never quite automate — matched by hand every month.",
          'FP&A, budgets and cash-flow live in spreadsheets next to the ERP, not in it.',
        ],
        build: [
          'Close trackers (like Close Cockpit)',
          'Reconciliation tools',
          'Cash-flow & reporting dashboards',
          'Consolidation & FP&A tools wired into your ERP',
        ],
        why: "Off-the-shelf reporting was built for someone else's chart of accounts. Yours fits your group, your entities, your data.",
      },
      ro: {
        label: 'Finanțe',
        hero: 'Închiderea de lună nu ar trebui să stea în douăsprezece fișiere Excel și în capul unui singur om.',
        problems: [
          'e-Factura și SAF-T (D406) nu se potrivesc niciodată curat cu contabilitatea — reconciliere manuală în fiecare lună.',
          'Închiderea de lună se sprijină pe un Excel fragil pe care îl înțelege un singur om.',
          'Închidere de grup / multi-entitate fără un tablou unic; pachetele de raportare asamblate manual.',
          'Reconciliere QR-bill / eBill între PostFinance, UBS, Raiffeisen.',
        ],
        build: [
          'Trackere de închidere (precum Close Cockpit)',
          'Instrumente de reconciliere',
          'Dashboard-uri de cash-flow și raportare',
          'Punți e-Factura / SAF-T către software-ul pe care îl ai deja',
        ],
        why: 'Raportarea de-a gata a fost construită pentru planul de conturi al altcuiva. Al tău se potrivește grupului tău, entităților tale, datelor tale.',
      },
      de: {
        label: 'Finanzen',
        hero: 'Ihr Abschluss sollte nicht in zwölf Excel-Tabellen und im Kopf einer einzigen Person leben.',
        problems: [
          'Der Monatsabschluss läuft auf einem fragilen Excel, das nur eine Person versteht.',
          'Konzern- / Multi-Entity-Abschluss ohne ein einziges Cockpit; Reporting-Packs von Hand zusammengestellt.',
          'QR-Rechnung / eBill-Abgleich über PostFinance, UBS, Raiffeisen — von Hand.',
          'bexio oder Abacus deckt das Reporting nicht ab, das Ihr Verwaltungsrat braucht.',
        ],
        build: [
          'Abschluss-Tracker (wie Close Cockpit)',
          'Abstimmungs-Tools',
          'Cashflow- & Reporting-Dashboards',
          'Konsolidierungs- & Reporting-Brücken in bexio / Abacus',
        ],
        why: 'Reporting von der Stange wurde für den Kontenplan eines anderen gebaut. Ihres passt zu Ihrer Gruppe, Ihren Einheiten, Ihren Daten.',
      },
    },
  },
  {
    slug: 'operations',
    demoKey: 'excel-to-app',
    flags: ['ro', 'ro', 'ch', 'ch'],
    i18n: {
      en: {
        label: 'Operations',
        hero: 'The spreadsheet that runs your company is one wrong click from disaster.',
        problems: [
          'The whole operation lives in Excel — fragile, un-shareable, error-prone.',
          'No real-time view; the real numbers are pulled from three systems by hand.',
          "Your tools don't talk — NetSuite, your WMS, Shopify, the CRM — so everything gets re-keyed.",
          'Growth outpaced the process; onboarding a new hire means teaching them the spreadsheet.',
        ],
        build: [
          'Excel → App conversions',
          'Live ops dashboards',
          'Workflow automation',
          'Stack sync between the tools you already run (NetSuite, Shopify, your WMS)',
        ],
        why: "We don't replace what works — we connect it and remove the manual glue.",
      },
      ro: {
        label: 'Operațiuni',
        hero: 'Fișierul Excel care îți ține firea în viață e la un click greșit de dezastru.',
        problems: [
          'Toată operațiunea trăiește în Excel — fragil, imposibil de partajat, predispus la erori.',
          'Coduri e-Transport / UIT gestionate manual — pentru firme, amenzile ajung la 20.000–100.000 lei.',
          '~60% dintre IMM-urile elvețiene nu obțin niciun avantaj competitiv din digitalizare (KOF/ETH) — „Insellösungen” deconectate, dublă introducere de date.',
          'Nicio imagine în timp real; cifre adunate manual din trei sisteme.',
        ],
        build: [
          'Conversii Excel → aplicație',
          'Dashboard-uri operaționale live',
          'Automatizare de fluxuri',
          'Sincronizare între uneltele pe care le folosești deja (bexio, KLARA, SmartBill, Saga)',
        ],
        why: 'Nu înlocuim ce funcționează — îl conectăm și scoatem lipiciul manual dintre unelte.',
      },
      de: {
        label: 'Betrieb',
        hero: 'Die Tabelle, die Ihre Firma steuert, ist einen Fehlklick vom Desaster entfernt.',
        problems: [
          'Der ganze Betrieb lebt in Excel — fragil, nicht teilbar, fehleranfällig.',
          '~60% der Schweizer KMU ziehen keinen Wettbewerbsvorteil aus der Digitalisierung (KOF/ETH) — getrennte Insellösungen, Doppelerfassung.',
          'Keine Echtzeit-Sicht; die echten Zahlen werden von Hand aus drei Systemen zusammengetragen.',
          'Ihre Tools reden nicht miteinander — bexio, KLARA, das CRM — alles wird doppelt erfasst.',
        ],
        build: [
          'Excel → App-Umwandlungen',
          'Live-Betriebs-Dashboards',
          'Workflow-Automatisierung',
          'Stack-Sync zwischen Tools, die Sie schon nutzen (bexio, KLARA, Abacus)',
        ],
        why: 'Wir ersetzen nicht, was funktioniert — wir verbinden es und entfernen den manuellen Klebstoff.',
      },
    },
  },
  {
    slug: 'sales',
    demoKey: 'sales-crm',
    flags: ['ro', 'ro', 'ch', 'both'],
    i18n: {
      en: {
        label: 'Sales',
        hero: 'Leads in your inbox. Quotes in Word. Pipeline in your head.',
        problems: [
          'Orders and leads arrive across email, phone and web forms — and slip through.',
          "A generic CRM (HubSpot, Salesforce) doesn't match how you actually sell.",
          "Quotes rebuilt from scratch every time; pricing logic lives in someone's spreadsheet.",
          "No clear, shared view of what's really in the pipeline.",
        ],
        build: [
          'Custom CRM shaped to your sales process',
          'Order / quote portals',
          'Lead routing',
          'Automated quote generation',
        ],
        why: 'Your sales motion is specific. A tool that forces a generic pipeline loses deals.',
      },
      ro: {
        label: 'Vânzări',
        hero: 'Comenzi pe WhatsApp. Oferte în Word. Pipeline-ul, în capul tău.',
        problems: [
          'Comenzile vin pe WhatsApp, telefon, email — și se pierd printre.',
          'CRM-urile generice de facturare (SmartBill) nu se potrivesc cu felul în care vinzi de fapt.',
          'CRM care nu se sincronizează; oferte refăcute de la zero de fiecare dată.',
          'Nicio imagine clară a ceea ce e în pipeline.',
        ],
        build: [
          'CRM custom, modelat pe procesul tău de vânzare',
          'Portaluri de comenzi / oferte',
          'Rutarea lead-urilor',
          'Generare automată de oferte',
        ],
        why: 'Felul în care vinzi e specific. O unealtă care impune un pipeline generic pierde contracte.',
      },
      de: {
        label: 'Vertrieb',
        hero: 'Leads im Postfach. Offerten in Word. Pipeline im Kopf.',
        problems: [
          'Bestellungen und Leads kommen über E-Mail, Telefon und Web-Formulare — und gehen verloren.',
          'Ein generisches CRM passt nicht dazu, wie Sie wirklich verkaufen.',
          'Offerten jedes Mal von Grund auf neu; die Preislogik steckt im Excel von jemandem.',
          'Keine klare, geteilte Sicht darauf, was wirklich in der Pipeline steckt.',
        ],
        build: [
          'Massgeschneidertes CRM, geformt nach Ihrem Verkaufsprozess',
          'Bestell- / Offerten-Portale',
          'Lead-Routing',
          'Automatische Offerten-Erstellung',
        ],
        why: 'Ihr Verkauf ist spezifisch. Ein Tool, das eine generische Pipeline erzwingt, verliert Abschlüsse.',
      },
    },
  },
  {
    slug: 'people',
    demoKey: 'people-onboarding',
    flags: ['ro', 'ro', 'ch', 'ch'],
    i18n: {
      en: {
        label: 'People',
        hero: 'Onboarding shouldn’t be a folder of PDFs and a dozen reminders.',
        problems: [
          'Onboarding is a folder of PDFs and a dozen reminders — nothing tracked end-to-end.',
          'PTO, documents and records scattered across tools with no self-service.',
          'Approvals lost in inboxes; no one can see where a request actually is.',
          'HR data spread across payroll, an ATS and three spreadsheets that never agree.',
        ],
        build: [
          'Onboarding flows',
          'Approval tools',
          'Employee self-service portal',
          'Records management',
        ],
        why: 'HR processes are specific to how you run. Off-the-shelf is built for the generic case, not yours.',
      },
      ro: {
        label: 'Resurse umane',
        hero: 'Onboarding-ul nu ar trebui să fie un folder de PDF-uri și o duzină de remindere.',
        problems: [
          'Ai trecut deja la REGES-ONLINE? Revisal a fost retras — onboarding-ul, contractele și aprobările manuale riscă acum amenzi de 15.000–20.000 lei.',
          'Concedii, documente, dosare împrăștiate, fără self-service.',
          'Lipsă acută de talent digital; onboarding-ul trebuie să fie rapid și fluid.',
          'Dacă AI-ul tău de HR atinge candidați din UE, poate intra sub regulile de risc ridicat din EU AI Act (din aug. 2026) — îl construim ca să nu fii expus.',
        ],
        build: [
          'Fluxuri de onboarding',
          'Instrumente de aprobare',
          'Portal de self-service pentru angajați',
          'Gestiunea dosarelor',
        ],
        why: 'Procesele de HR sunt locale (legislația muncii din RO ≠ CH). Soluțiile de-a gata nu sunt făcute pentru niciuna.',
      },
      de: {
        label: 'Personal',
        hero: 'Onboarding sollte kein Ordner voller PDFs und ein Dutzend Erinnerungen sein.',
        problems: [
          'Onboarding ist ein Ordner voller PDFs und ein Dutzend Erinnerungen — nichts wird durchgängig verfolgt.',
          'Ferien, Dokumente, Dossiers verstreut über Tools, ohne Self-Service.',
          'Akuter Mangel an digitalen Talenten; Onboarding muss schnell und reibungslos sein.',
          'Berührt Ihre HR-KI EU-Kandidaten, kann sie unter die Hochrisiko-Regeln des EU AI Act fallen (ab Aug. 2026) — wir bauen sie so, dass Sie nicht exponiert sind.',
        ],
        build: [
          'Onboarding-Abläufe',
          'Freigabe-Tools',
          'Self-Service-Portal für Mitarbeitende',
          'Dossier-Verwaltung',
        ],
        why: 'HR-Prozesse sind spezifisch dafür, wie Sie arbeiten. Lösungen von der Stange sind für den Allgemeinfall gebaut, nicht für Ihren.',
      },
    },
  },
  {
    slug: 'procurement',
    demoKey: 'procurement',
    flags: ['ro', 'ro', 'ch', 'ch'],
    i18n: {
      en: {
        label: 'Procurement',
        hero: 'You approve POs by email and find out a supplier was risky too late.',
        problems: [
          'Supplier vetting done by hand — credit, status, insurance, risk — if it gets done at all.',
          'Purchase approvals lost in inboxes; no real spend visibility until the invoice lands.',
          'Rising material cost & availability; supplier risk needs monitoring, not guessing.',
          'Spend data spread across the ERP, email threads and a spreadsheet.',
        ],
        build: [
          'Supplier verification tools',
          'PO & approval workflows',
          'Spend-control dashboards',
          'Supplier-risk monitors',
        ],
        why: 'Spend control is about your suppliers and your thresholds — generic procurement suites ignore both.',
      },
      ro: {
        label: 'Achiziții',
        hero: 'Aprobi comenzi pe email și afli prea târziu că un furnizor era riscant.',
        problems: [
          'Verificarea furnizorilor făcută manual — status CUI, TVA, insolvență, obligație de e-Factura.',
          'Aprobările de achiziție pierdute prin inbox-uri; nicio vizibilitate a cheltuielilor.',
          'Cost și disponibilitate în creștere la materii prime; riscul de furnizor trebuie monitorizat, nu ghicit.',
          'Management multilingv al furnizorilor (DE / FR / IT).',
        ],
        build: [
          'Instrumente de verificare a furnizorilor',
          'Fluxuri de comenzi și aprobări',
          'Dashboard-uri de control al cheltuielilor',
          'Monitoare de risc de furnizor',
        ],
        why: 'Controlul cheltuielilor ține de furnizorii tăi și de pragurile tale — suitele generice de achiziții le ignoră pe ambele.',
      },
      de: {
        label: 'Einkauf',
        hero: 'Sie genehmigen Bestellungen per E-Mail und erfahren zu spät, dass ein Lieferant riskant war.',
        problems: [
          'Lieferantenprüfungen von Hand — Bonität, Status, Versicherung, Risiko — falls überhaupt.',
          'Bestellfreigaben gehen im Posteingang verloren; keine echte Ausgaben-Transparenz, bis die Rechnung kommt.',
          'Steigende Rohstoffkosten & -verfügbarkeit; Lieferantenrisiko gehört überwacht, nicht geraten.',
          'Mehrsprachige Lieferantenverwaltung (DE / FR / IT).',
        ],
        build: [
          'Tools zur Lieferantenprüfung',
          'Bestell- & Freigabe-Workflows',
          'Dashboards zur Ausgabenkontrolle',
          'Lieferantenrisiko-Monitore',
        ],
        why: 'Ausgabenkontrolle dreht sich um Ihre Lieferanten und Ihre Schwellen — generische Einkaufs-Suiten ignorieren beides.',
      },
    },
  },
]

// Departamente localizate: structură plată per limbă (cum o consumă paginile).
export function getDepartments(locale = DEFAULT_LOCALE) {
  return DEPARTMENTS.map((d) => {
    const t = d.i18n[locale] || d.i18n[DEFAULT_LOCALE]
    return {
      slug: d.slug,
      demoKey: d.demoKey,
      label: t.label,
      hero: t.hero,
      build: t.build,
      why: t.why,
      problems: t.problems.map((text, i) => ({ flag: d.flags[i], text })),
    }
  })
}

export function getDepartment(slug, locale = DEFAULT_LOCALE) {
  return getDepartments(locale).find((d) => d.slug === slug)
}

// Listă de slug-uri (agnostică de limbă) — pentru rute/sitemap.
export const DEPARTMENT_SLUGS = DEPARTMENTS.map((d) => d.slug)
